import { ref, reactive, computed, watch, nextTick } from 'vue';
import store from '@/store';
import { config } from '@/config.js';
import { totalesPagina, totalesDesdeRespuesta, totalesVacios } from '@/composables/admin/eventos/resumenVentasAgregados';

/**
 * useResumenVentas — estado, filtros, paginación y totales de la pestaña
 * "Resumen de ventas" del listado de eventos.
 *
 * Endpoint: `back.events.resumen-ventas` → GET /eventos/resumen-ventas (v1).
 * El payload que viaja al backend es EXACTAMENTE el que mandaba el componente
 * legacy (`{ filters: {...} }`): cambiarlo obligaría a tocar API, y el
 * resumen de ventas no es candidato a v2.
 *
 * El backend responde `{ data: [{ ...evento }], meta: { current_page,
 * last_page, per_page, total } }` dentro de `res.data` (la acción resuelve la
 * respuesta de axios, no el body).
 *
 * @param {{
 *   fetcher?: Function,
 *   perPageInicial?: number,
 *   filtrosIniciales?: Object
 * }} [opciones]
 *   fetcher(filters) — inyectable para tests; recibe los filtros PELADOS (no un
 *   `{ filters }`) y debe resolver `{ data: { data, meta } }`. La acción Vuex
 *   real se envuelve en `{ filters }` por dentro.
 * @returns {Object} estado del resumen de ventas.
 */
export function useResumenVentas({ fetcher, perPageInicial = 10, filtrosIniciales } = {}) {
    /**
     * Los filtros son el MISMO objeto que viaja al backend: se mutan directo
     * desde los inputs del formulario. `page` y `per_page` los gobierna el
     * TableFooter a través de `model`.
     */
    const filtros = reactive({
        per_page: perPageInicial,
        page: 1,
        search: '',
        search_teatro_id: '',
        taquillero_id: '',
        teatro_company_id: '',
        fecha_inicio: null,
        fecha_fin: null,
        pasados: false,
        actuales: true,
        ...(filtrosIniciales || {})
    });

    const eventos = ref([]);
    const meta = ref({});
    /**
     * Totales globales del rango filtrado que entrega el backend (`payload.totales`).
     * `null` mientras no vengan: entonces las tarjetas KPI muestran ceros.
     */
    const totalesDelRango = ref(null);
    // `loading` es el primer render (va a skeleton). `refrescando` son las
    // recargas con datos ya en pantalla: no queremos volver a esqueletizar algo
    // que el usuario está leyendo, solo atenuarlo.
    const loading = ref(true);
    const refrescando = ref(false);
    const error = ref(null);
    // true sólo cuando la ÚLTIMA petición terminó bien: permite distinguir
    // "cargó y no hay resultados" de "no cargó".
    const cargado = ref(false);

    // La compañía se guarda como OBJETO (es lo que emite BaseMultiselect y lo
    // que el legacy mantenía en `selectedCompania`); de ahí sale el id que
    // viaja en `filters.teatro_company_id`.
    const companiaSeleccionada = ref(null);

    // Secuencia de peticiones: una respuesta lenta anterior no puede pisar el
    // estado de los filtros vigentes.
    let ultimaPeticion = 0;

    // Evita la recarga duplicada cuando el término de búsqueda lo cambia un
    // proceso (limpiar) que ya recarga por su cuenta.
    let silenciarBusqueda = false;

    /**
     * Petición por defecto: despacha la acción Vuex del endpoint con el MISMO
     * contrato que recibe un `fetcher` inyectado, o sea los filtros pelados.
     *
     * Este parámetro estaba destructurado como `{ filters }` mientras el
     * llamador (`getEventos`) pasaba los filtros directamente: la acción salía
     * con `filters: undefined`, `api.call` la normalizaba a `{}` y el backend
     * respondía SIEMPRE sin filtros. El listado ignoraba la búsqueda, las
     * fechas, los catálogos, los toggles de periodo y la paginación.
     *
     * @param {Object} filters Filtros que viajan al backend.
     */
    async function dispatchResumen(filters) {
        return store.dispatch(config.getVuexAlias('back.events.resumen-ventas'), { filters });
    }

    const doFetch = fetcher || dispatchResumen;

    async function getEventos() {
        const peticion = ++ultimaPeticion;

        if (!cargado.value) {
            loading.value = true;
        } else {
            refrescando.value = true;
        }
        error.value = null;

        try {
            const res = await doFetch({ ...filtros });

            if (peticion !== ultimaPeticion) return;

            const payload = (res && res.data) || {};
            eventos.value = Array.isArray(payload.data) ? payload.data : [];
            meta.value = payload.meta || {};
            // Totales de TODO el rango filtrado (los pide backend en la tarea
            // 1219545939875545). Si no vienen, `null` y las tarjetas van a 0.
            totalesDelRango.value = totalesDesdeRespuesta(payload);
            cargado.value = true;
        } catch (e) {
            if (peticion !== ultimaPeticion) return;

            // Un fallo NO es un listado vacío: la vista pinta el error con
            // opción de reintentar en vez del mensaje de "sin resultados".
            error.value = e;
            eventos.value = [];
            meta.value = {};
            totalesDelRango.value = null;
            if (process.env.NODE_ENV !== 'production') console.error(e);
        } finally {
            if (peticion === ultimaPeticion) {
                loading.value = false;
                refrescando.value = false;
            }
        }
    }

    /**
     * Búsqueda rápida: filtra sola en cuanto llega un término nuevo y el botón
     * BUSCAR la aplica al instante desde la primera página.
     *
     * El debounce NO está aquí sino en `SearchComponent` (500 ms desde la
     * última tecla): es el contrato de ese input. Duplicarlo aquí sumaba dos
     * esperas y la búsqueda tardaba un segundo.
     */
    function buscar() {
        filtros.page = 1;
        return getEventos();
    }

    /**
     * Los checkboxes de periodo son mutuamente excluyentes, igual que en el
     * legacy: marcar uno desmarca el otro. Ninguno marcado es un estado válido
     * (lo deja así "Limpiar filtros") y el backend lo interpreta como "todo".
     *
     * @param {string} periodo 'pasados' | 'actuales'.
     * @param {boolean} activo Estado nuevo del checkbox.
     */
    function alternarPeriodo(periodo, activo) {
        if (periodo === 'pasados') {
            filtros.pasados = activo;
            if (activo) filtros.actuales = false;
        } else if (periodo === 'actuales') {
            filtros.actuales = activo;
            if (activo) filtros.pasados = false;
        }

        buscar();
    }

    /**
     * Limpia todos los filtros y recarga. Mismo comportamiento que el botón
     * legacy: deja los dos checkboxes en falso.
     */
    function limpiar() {
        filtros.per_page = perPageInicial;
        filtros.page = 1;
        // Limpiar la búsqueda dispara el watcher (que recargaría) y además
        // recarga `buscar()`: se silencia durante el tick para no pedir dos
        // veces lo mismo.
        silenciarBusqueda = true;
        filtros.search = '';
        filtros.search_teatro_id = '';
        filtros.taquillero_id = '';
        filtros.teatro_company_id = '';
        filtros.fecha_inicio = null;
        filtros.fecha_fin = null;
        filtros.pasados = false;
        filtros.actuales = false;
        companiaSeleccionada.value = null;

        const peticion = buscar();
        nextTick(() => { silenciarBusqueda = false; });

        return peticion;
    }

    /**
     * La compañía llega como objeto desde BaseMultiselect (o `null` al
     * deseleccionar). Se traduce a id para los filtros.
     *
     * @param {Object|null} compania
     */
    function seleccionarCompania(compania) {
        companiaSeleccionada.value = compania;
        // `BaseMultiselect` emite el ID (no el objeto); el legacy recibía el
        // objeto. Se aceptan las dos formas igual que en teatro/taquillero.
        filtros.teatro_company_id = aIdNumerico(compania);
    }

    /**
     * Normaliza el valor de un desplegable a id numérico.
     *
     * Llegan las dos formas: `BaseMultiselect` emite el OBJETO de la opción
     * (o `null` al deseleccionar) y un `<select>` nativo emite el value del DOM
     * como string ('1268'). El legacy mandaba el id numérico, así que se
     * respeta ese contrato: número si se puede, cadena vacía si no hay
     * selección.
     *
     * @param {Object|string|number|null} value
     * @returns {number|string}
     */
    function aIdNumerico(value) {
        const bruto = value && typeof value === 'object' ? value.id : value;

        if (bruto === '' || bruto === null || bruto === undefined) return '';

        const numero = Number(bruto);

        return Number.isNaN(numero) ? bruto : numero;
    }

    /**
     * @param {string|number} value Id del teatro recinto seleccionado.
     */
    function seleccionarTeatro(value) {
        filtros.search_teatro_id = aIdNumerico(value);
    }

    /**
     * @param {string|number} value Id del taquillero seleccionado.
     */
    function seleccionarTaquillero(value) {
        filtros.taquillero_id = aIdNumerico(value);
    }

    /** Sumas de la PÁGINA cargada (pie de la tabla). */
    const totalesPaginaActual = computed(() => totalesPagina(eventos.value));

    /**
     * Totales de las tarjetas KPI: el global del rango si el backend lo manda,
     * y ceros mientras no lo haga. NUNCA la suma de la página: parecería el
     * total del rango sin serlo.
     */
    const totales = computed(() => totalesDelRango.value || totalesVacios());

    /** `true` cuando las tarjetas muestran el total real del rango. */
    const totalesGlobales = computed(() => totalesDelRango.value !== null);
    const totalPaginas = computed(() => {
        const last = meta.value && meta.value.last_page;
        if (typeof last === 'number') return Math.max(1, last);
        const total = meta.value && meta.value.total;
        return Math.max(1, Math.ceil((typeof total === 'number' ? total : 0) / (filtros.per_page || 1)));
    });

    /**
     * Filtros tal como los consume la descarga (resumen y arqueo de caja).
     * Mismo contrato de nombres que el legacy para no romper los endpoints.
     */
    const filtrosParaDescarga = computed(() => ({
        del: filtros.fecha_inicio,
        al: filtros.fecha_fin,
        teatro_id: filtros.search_teatro_id,
        taquillero_id: filtros.taquillero_id,
        teatro_company_id: filtros.teatro_company_id,
        pasado: filtros.pasados,
        actual: filtros.actuales,
        search_teatro_id: filtros.search_teatro_id,
        fecha_inicio: filtros.fecha_inicio,
        fecha_fin: filtros.fecha_fin
    }));

    /** Modelo compatible con TableFooter (value = { page, per_page }). */
    function aplicarPaginacion(val) {
        if (!val) return;
        const nextPage = val.page !== undefined ? val.page : filtros.page;
        const nextPerPage = val.per_page !== undefined ? parseInt(val.per_page, 10) : filtros.per_page;

        filtros.per_page = nextPerPage;
        filtros.page = nextPage;

        return getEventos();
    }

    const model = computed({
        get: () => ({ page: filtros.page, per_page: filtros.per_page }),
        set: aplicarPaginacion
    });

    // La búsqueda rápida siempre vuelve a la primera página. `silenciarBusqueda`
    // evita la recarga duplicada cuando quien cambia el término es `limpiar()`
    // (que ya recarga por su cuenta).
    watch(() => filtros.search, () => {
        if (silenciarBusqueda) return;
        buscar();
    });

    return {
        filtros,
        eventos,
        meta,
        loading,
        refrescando,
        error,
        cargado,
        companiaSeleccionada,
        totales,
        totalesPagina: totalesPaginaActual,
        totalesGlobales,
        totalPaginas,
        filtrosParaDescarga,
        model,
        getEventos,
        buscar,
        limpiar,
        alternarPeriodo,
        seleccionarCompania,
        seleccionarTeatro,
        seleccionarTaquillero,
        aplicarPaginacion
    };
}
